import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";

import { toast } from "react-hot-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AiFillSafetyCertificate } from "react-icons/ai";
import {
  getChatbotVerifiedAnswers,
  deleteVerifiedQA,
  downloadVerifiedQA,
  storeBulkCache,
} from "../../services/api/CacheService";
import Markdown from "../Markdown/Markdown";
import { MdDeleteOutline } from "react-icons/md";

function KnowledgeBaseVerifiedQA() {
  const location = useLocation();
  const [stateLocal] = useState(location.state);
  const [verifiedQA, setVerifiedQA] = useState(null);
  const [file, setFile] = useState(null);

  const handleGetVerifiedQA = async () => {
    const res = await getChatbotVerifiedAnswers(stateLocal.config.chatbot_name);
    if (res) {
      setVerifiedQA(res.cached_qa);
    }
  };

  const handleDeleteQA = async (doc_id) => {
    const res = await deleteVerifiedQA(stateLocal.config.chatbot_name, doc_id);
    if (res) {
      const newVerifiedQA = verifiedQA.filter((item) => item.id !== doc_id);
      setVerifiedQA(newVerifiedQA);
      toast.success("Verified QA deleted successfully");
    }
  };

  const handleDownloadQA = async () => {
    const res = await downloadVerifiedQA(stateLocal.config.chatbot_name);
    if (res) {
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "verified-qa.xlsx");
      document.body.appendChild(link);
      link.click();
    }
  };

  const handleUploadFile = async () => {
    const res = await storeBulkCache(file);
    if (res) {
      toast.success("File uploaded successfully");
      setFile(null);
      await handleGetVerifiedQA();
    }
  };

  return (
    <Drawer>
      <DrawerTrigger>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <button
                className="flex items-center gap-2 border border-primary rounded p-3 text-primary hover:text-white hover:bg-primary"
                onClick={handleGetVerifiedQA}
              >
                <AiFillSafetyCertificate className="text-xl" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">View Verified QA</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </DrawerTrigger>
      <DrawerContent className="h-5/6">
        <DrawerHeader>
          <DrawerTitle>Verified Answers</DrawerTitle>
        </DrawerHeader>
        <DrawerDescription className="h-full overflow-y-scroll ">
          <div className="px-5">
            <div className="flex justify-end w-full gap-2">
              <AlertDialog>
                <AlertDialogTrigger>
                  <Button>Upload</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      <div>
                        This action cannot be undone. This will permanently
                        delete your current verified qa and replace with the new
                        file.
                      </div>
                      <div className="mt-5">
                        <Input
                          id="xlsxfile"
                          type="file"
                          accept=".xlsx" // Add accept attribute to accept only xlsx files
                          onChange={(event) =>
                            setFile(event.target.files && event.target.files[0])
                          }
                        />
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleUploadFile}>
                      Yes, I know
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button onClick={handleDownloadQA}>Download .xlsx</Button>
              <AlertDialog>
                <AlertDialogTrigger>
                  <Button className="bg-red-500 hover:bg-red-600">
                    Invalidate all answer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      your data from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction>Continue</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {verifiedQA && verifiedQA.length > 0 ? (
              <table className="text-sm text-left mt-10 w-full">
                <thead className="text-xs text-white uppercase bg-primary">
                  <tr>
                    <th scope="col" className="p-3">
                      Question
                    </th>
                    <th scope="col" className="p-3">
                      Answer
                    </th>
                    <th scope="col" className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {verifiedQA &&
                    verifiedQA.map((qa, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="p-3 text-left">{qa.query}</td>
                        <td className="p-3">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="text-left cursor-zoom-in">
                                <Markdown
                                  text={qa.answer.slice(0, 500) + "..."}
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs p-2">
                                  <Markdown text={qa.answer} />
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        <td className="p-3">
                          <MdDeleteOutline
                            className="text-red-500 cursor-pointer"
                            onClick={() => handleDeleteQA(qa.id)}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : (
              <p className="mt-5">No verified QA found</p>
            )}
          </div>
        </DrawerDescription>
      </DrawerContent>
    </Drawer>
  );
}

export default KnowledgeBaseVerifiedQA;
